import React, { useState, useRef, useEffect, useCallback } from 'react';
import { INTERNAL_SURFACES, EXTERNAL_SUBSTRATES, PRELIMINARY_ITEMS } from './areaConstants';
import SurfaceBlock from './SurfaceBlock';
import { IS_DESKTOP, emptyEntry, emptyInternal, emptyExternal, useAutoAdvance, uploadPhoto } from './areaFormUtils';
import { entryM2 } from './calcUtils';
import ConfirmModal from './ConfirmModal';
import AccessTags from './AccessTags';
import DirectionPicker, { headingToDirection } from './DirectionPicker';
import Icon from './Icons';
import { showToast } from './Toast';

const safe = (arr) => Array.isArray(arr) ? arr : [];

// ── Count input — stores as text, validates on blur (prevents snap-to-1) ────
function CountInput({ value, onChange, onClick }) {
  const [text, setText] = React.useState(String(value ?? ''));
  React.useEffect(() => { setText(String(value ?? '')); }, [value]);
  return (
    <input className="rg-count-input" type="text" inputMode="decimal" value={text}
      onClick={onClick}
      onChange={e => setText(e.target.value)}
      onBlur={() => {
        const v = parseFloat(text);
        onChange(!isNaN(v) && v >= 1 ? v : (value ?? 1));
      }} />
  );
}

// ── Config per area type ────────────────────────────────────────────────────
const TYPE_CONFIG = {
  internal: { icon: '🏠', label: 'Internal', surfaces: INTERNAL_SURFACES, nameHint: 'e.g. Sunken Lounge, Bedroom 1', empty: emptyInternal },
  external: { icon: '🏗️', label: 'External', surfaces: EXTERNAL_SUBSTRATES, nameHint: 'e.g. North Elevation, Front Fence', empty: emptyExternal },
};

// ── CompoundMeasureBlock ─────────────────────────────────────────────────────
// A compound surface (Balconies, Rooms etc.) — name it in the add phase,
// then during measure: add sub-surfaces → measure each → set count → Done.
function CompoundMeasureBlock({ group, data, surfaceList, onUpdateGroup, onRemoveGroup, onDuplicateGroup, onAddEntry, onUpdateEntry, onRemoveEntry, onSettingChange, onConditionChange, isActive, onDone, onSave, saving, areaType, substrateHasPhoto, onCaptureForSubstrate }) {
  const [dropdown, setDropdown] = useState('');
  const [customName, setCustomName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const cardRef = useRef(null);
  const isCustom = dropdown === 'Other' || dropdown === 'Custom Surface Name';

  const groupEntries = safe(data.surfaceEntries).filter(e => e.repeatGroupId === group.id);
  const groupSubstrates = [...new Set(groupEntries.map(e => e.substrate))];
  const hasData = groupEntries.some(e => entryM2(e.substrate, e) > 0);

  // Phase: 'setup' (fresh — no entries yet) | 'measuring' | 'count' | 'edit' (re-opened with data)
  const [phase, setPhase] = useState(() => hasData ? 'edit' : 'setup');
  const [expandedSub, setExpandedSub] = useState(null);

  // When re-activated with existing data, jump to edit mode — not setup
  React.useEffect(() => {
    if (!isActive) { setExpandedSub(null); }
    else if (hasData) setPhase('edit');
    else if (groupSubstrates.length === 0) setPhase('setup');
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll card into view when phase changes to 'count'
  React.useEffect(() => {
    if (phase === 'count' && cardRef.current) {
      setTimeout(() => {
        const headerHeight = 64;
        const top = cardRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 80);
    }
  }, [phase]);

  const handleAddSub = () => {
    const name = isCustom ? customName.trim() : dropdown;
    if (!name) return;
    onAddEntry(name, group.id);
    setDropdown(''); setCustomName('');
    setExpandedSub(name);
  };

  const removeSub = (sub) => {
    groupEntries.filter(e => e.substrate === sub).forEach(e => onRemoveEntry(e.id));
    if (expandedSub === sub) setExpandedSub(null);
  };

  if (!isActive) return null; // only rendered during measure phase

  return (
    <div className="cmb-card" ref={cardRef}>
      {showDeleteConfirm && (
        <ConfirmModal title="Remove surface" message={`Remove "${group.name}" and all its sub-surfaces?`}
          confirmLabel="Remove" danger
          onConfirm={() => { setShowDeleteConfirm(false); onRemoveGroup(group.id); }}
          onCancel={() => setShowDeleteConfirm(false)} />
      )}
      {/* Header */}
      <div className="cmb-header">
        <span className="cmb-icon">⊞</span>
        <input className="cmb-name-input" value={group.name}
          placeholder="e.g. Balconies, Rooms, Garages"
          onChange={e => onUpdateGroup(group.id, 'name', e.target.value)} />
        {onDuplicateGroup && (
          <button className="cmb-copy-btn"
            onClick={(e) => { e.stopPropagation(); onDuplicateGroup(group.id); }}
            title="Duplicate this group with all measurements">
            Copy
          </button>
        )}
      </div>

      {/* Phase: setup — add sub-surfaces */}
      {phase === 'setup' && (
        <div className="cmb-body">
          <div className="cmb-phase-label">What surfaces does one {group.name || 'unit'} have?</div>
          {groupSubstrates.length > 0 && (
            <div className="cmb-sub-list">
              {groupSubstrates.map(sub => (
                <div key={sub} className="s2s-selected-surface">
                  <span>✓ {sub}</span>
                  <button onClick={() => removeSub(sub)} aria-label={`Remove ${sub}`}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="rg-add-surface">
            <select value={dropdown} onChange={e=>{setDropdown(e.target.value);setCustomName('');}}>
              <option value="">{groupSubstrates.length === 0 ? 'Add a surface...' : '＋ Add another...'}</option>
              {surfaceList.filter(s => s !== 'Custom Surface Name').map(s => <option key={s} value={s}>{s}</option>)}
              <option value="Custom Surface Name">Other (custom)...</option>
            </select>
            {!isCustom && <button onClick={handleAddSub} disabled={!dropdown}>Add</button>}
          </div>
          {isCustom && (
            <div className="s2s-custom-input" style={{marginTop:4}}>
              <input autoFocus placeholder="Surface name..." value={customName}
                onChange={e => setCustomName(e.target.value)}
                onKeyDown={e => e.key==='Enter' && customName.trim() && handleAddSub()} />
              <button onClick={handleAddSub} disabled={!customName.trim()}>Add</button>
            </div>
          )}
          {groupSubstrates.length > 0 && (
            <button className="af-start-measuring-btn" style={{marginTop:12}}
              onClick={() => { setPhase('measuring'); setExpandedSub(groupSubstrates[0]); }}>
              <Icon name="ruler" size={16} /> Start measuring
            </button>
          )}
        </div>
      )}

      {/* Phase: measuring — step through sub-surfaces */}
      {phase === 'measuring' && (
        <div className="cmb-body">
          {groupSubstrates.map((sub, idx) => {
            const isLast = idx === groupSubstrates.length - 1;
            return (
              <div key={`${group.id}-${sub}`}>
                <SurfaceBlock substrate={sub} data={data} groupId={group.id} areaType={areaType}
                  isExpanded={expandedSub === sub}
                  onToggleExpand={() => setExpandedSub(s => s === sub ? null : sub)}
                  onUpdate={onUpdateEntry} onAdd={onAddEntry} onRemove={onRemoveEntry}
                  onSettingChange={onSettingChange} onConditionChange={onConditionChange}
                  substrateHasPhoto={substrateHasPhoto} onCaptureForSubstrate={onCaptureForSubstrate}
                  onRemoveSurface={() => removeSub(sub)} />
                {expandedSub === sub && (
                  <button className={`af-next-surface-btn${isLast ? ' af-next-surface-btn-last' : ''}`}
                    onClick={() => {
                      if (isLast) { setPhase('count'); setExpandedSub(null); }
                      else setExpandedSub(groupSubstrates[idx + 1]);
                    }}>
                    {isLast ? 'Next: How many? →' : `Next: ${groupSubstrates[idx + 1]} →`}
                  </button>
                )}
              </div>
            );
          })}
          {/* Add more sub-surfaces during measuring */}
          <div className="rg-add-surface" style={{marginTop:8}}>
            <select value={dropdown} onChange={e=>{setDropdown(e.target.value);setCustomName('');}}>
              <option value="">＋ Add another surface...</option>
              {surfaceList.filter(s => s !== 'Custom Surface Name').map(s => <option key={s} value={s}>{s}</option>)}
              <option value="Custom Surface Name">Other (custom)...</option>
            </select>
            {!isCustom && <button onClick={handleAddSub} disabled={!dropdown}>Add</button>}
          </div>
          {isCustom && (
            <div className="s2s-custom-input" style={{marginTop:4}}>
              <input autoFocus placeholder="Surface name..." value={customName}
                onChange={e => setCustomName(e.target.value)}
                onKeyDown={e => e.key==='Enter' && customName.trim() && handleAddSub()} />
              <button onClick={handleAddSub} disabled={!customName.trim()}>Add</button>
            </div>
          )}
        </div>
      )}

      {/* Phase: count — how many units? */}
      {phase === 'count' && (
        <div className="cmb-body">
          <div className="cmb-phase-label">How many {group.name || 'units'}?</div>
          <div className="cmb-count-row">
            <CountInput value={group.count || 1} onChange={v => onUpdateGroup(group.id, 'count', v)} />
            <span className="cmb-count-label">× {group.name || 'unit'}</span>
          </div>
          <button className="af-next-surface-btn af-next-surface-btn-last" style={{marginTop:16}}
            disabled={saving}
            onClick={async () => { await onSave(); onDone(); }}>
            {saving ? <><span className="spinner spinner-sm spinner-white"></span> Saving…</> : <><Icon name="check" size={16} /> Done</>}
          </button>
        </div>
      )}

      {/* Phase: edit — re-opened group, direct access to any sub-surface */}
      {phase === 'edit' && (
        <div className="cmb-body">
          <div className="cmb-count-row" style={{marginBottom:12}}>
            <CountInput value={group.count || 1} onChange={v => onUpdateGroup(group.id, 'count', v)} />
            <span className="cmb-count-label">× {group.name || 'unit'}</span>
          </div>
          {groupSubstrates.map(sub => (
            <div key={`${group.id}-${sub}`}>
              <SurfaceBlock substrate={sub} data={data} groupId={group.id} areaType={areaType}
                isExpanded={expandedSub === sub}
                onToggleExpand={() => setExpandedSub(s => s === sub ? null : sub)}
                onUpdate={onUpdateEntry} onAdd={onAddEntry} onRemove={onRemoveEntry}
                onSettingChange={onSettingChange} onConditionChange={onConditionChange}
                substrateHasPhoto={substrateHasPhoto} onCaptureForSubstrate={onCaptureForSubstrate}
                onRemoveSurface={() => removeSub(sub)} />
            </div>
          ))}
          <div className="rg-add-surface" style={{marginTop:8}}>
            <select value={dropdown} onChange={e=>{setDropdown(e.target.value);setCustomName('');}}>
              <option value="">＋ Add another surface...</option>
              {surfaceList.filter(s => s !== 'Custom Surface Name').map(s => <option key={s} value={s}>{s}</option>)}
              <option value="Custom Surface Name">Other (custom)...</option>
            </select>
            {!isCustom && <button onClick={handleAddSub} disabled={!dropdown}>Add</button>}
          </div>
          {isCustom && (
            <div className="s2s-custom-input" style={{marginTop:4}}>
              <input autoFocus placeholder="Surface name..." value={customName}
                onChange={e => setCustomName(e.target.value)}
                onKeyDown={e => e.key==='Enter' && customName.trim() && handleAddSub()} />
              <button onClick={handleAddSub} disabled={!customName.trim()}>Add</button>
            </div>
          )}
          <button className="af-next-surface-btn af-next-surface-btn-last" style={{marginTop:16}}
            disabled={saving}
            onClick={async () => { await onSave(); onDone(); }}>
            {saving ? <><span className="spinner spinner-sm spinner-white"></span> Saving…</> : <><Icon name="check" size={16} /> Done</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Unified AreaForm ────────────────────────────────────────────────────────
export default function AreaForm({ area, type, projectId, projectHasHero, onSave, onBack, onNewArea, nextArea, areaPosition }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.external;
  const isNew = area?.isNew;
  const desktop = IS_DESKTOP();

  const [data, setData] = useState(() => isNew ? { ...cfg.empty(), name: area?.name || '' } : { ...area });
  const [uploadCount, setUploadCount] = useState(0); // number of uploads in flight
  const [saving, setSaving] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [surfDropdown, setSurfDropdown] = useState("");
  const [surfCustomName, setSurfCustomName] = useState("");
  const [newGroupId, setNewGroupId] = useState(null); // auto-focus new compound chip
  const [activeItemKey, setActiveItemKey] = useState(null); // null | 'surface:X' | 'group:id'
  const [measuresDone, setMeasuresDone] = useState(() => !isNew && area?.status === 'finished');
  const surfaceRefs = useRef({});
  const notesRef = useRef(null);
  const dataPanelRef = useRef(null); // desktop scroll container

  // Scroll active surface into view
  const expandedSurface = activeItemKey?.startsWith('surface:') ? activeItemKey.slice(8) : null;
  useEffect(() => {
    if (!expandedSurface) return;
    const timer = setTimeout(() => {
      const el = surfaceRefs.current[expandedSurface];
      if (el) scrollToEl(el);
    }, 110);
    return () => clearTimeout(timer);
  }, [expandedSurface]);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [nameError, setNameError] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const rawPMAdded = (!isNew && Array.isArray(area?.pmAddedSurfaces) && area.pmAddedSurfaces.length > 0) ? area.pmAddedSurfaces : [];
  const rawPMSurfaces = (!isNew && Array.isArray(area?.pmSurfaces) && area.pmSurfaces.length > 0) ? area.pmSurfaces : [];
  const pmAddedSurfaces = [...new Set([...rawPMAdded, ...rawPMSurfaces])];
  // Only alert for PM-added surfaces that still have no real measurements
  const unmeasuredPMSurfaces = pmAddedSurfaces.filter(s => {
    const entries = safe(area?.surfaceEntries).filter(e => e.substrate === s);
    if (entries.length === 0) return true; // not even added yet
    // Check if all entries are empty (no dimensions entered)
    return entries.every(e => !e.length && !e.width && !e.height && !e.quantity && !e.doorWidth && !e.doorHeight);
  });
  const [showPMAlert, setShowPMAlert] = useState(unmeasuredPMSurfaces.length > 0);

  // Auto-inject PM-flagged surfaces as empty entries if not already present
  useEffect(() => {
    if (pmAddedSurfaces.length === 0) return;
    setData(prev => {
      const existingEntries = new Set(safe(prev.surfaceEntries).map(e => e.substrate));
      const existingOrder = new Set((prev.itemOrder || []).filter(i => i.type === 'surface').map(i => i.substrate));
      const toAdd = pmAddedSurfaces.filter(s => !existingEntries.has(s));
      const toAddOrder = pmAddedSurfaces.filter(s => !existingOrder.has(s));
      if (toAdd.length === 0 && toAddOrder.length === 0) return prev;
      return {
        ...prev,
        surfaceEntries: [...safe(prev.surfaceEntries), ...toAdd.map(s => emptyEntry(s))],
        itemOrder: [...(prev.itemOrder || []), ...toAddOrder.map(s => ({ type: 'surface', substrate: s }))],
      };
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const formRef = useRef(null);
  const autoAdvance = useAutoAdvance(formRef);
  const dirty = useRef(false);
  const compassHeading = useRef(null);
  const compassListening = useRef(false);
  const compassPrompted = useRef(false);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  // Request compass permission and start listening — triggered once on the
  // first user interaction with the form (see effect below), not on camera tap
  const startCompassIfNeeded = useCallback(() => {
    if (compassListening.current) return;
    const startCompass = () => {
      compassListening.current = true;
      window.addEventListener('deviceorientation', (e) => {
        const heading = e.webkitCompassHeading ?? (e.alpha != null ? (360 - e.alpha) % 360 : null);
        if (heading != null) compassHeading.current = heading;
      }, true);
    };
    // iOS 13+ requires an explicit user-gesture permission request
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission().then(r => { if (r === 'granted') startCompass(); }).catch(() => {});
    } else {
      startCompass();
    }
  }, []);

  // Request compass permission ONCE on the first real user interaction with the
  // form. iOS requires requestPermission() to run inside a user gesture, so this
  // is attached to the first pointer/touch — deliberately decoupled from the
  // camera buttons so the motion dialog can never consume the camera's gesture.
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const onFirstInteract = () => {
      if (compassPrompted.current) return;
      compassPrompted.current = true;
      startCompassIfNeeded();
      el.removeEventListener('pointerdown', onFirstInteract);
      el.removeEventListener('touchstart', onFirstInteract);
    };
    el.addEventListener('pointerdown', onFirstInteract, { once: false });
    el.addEventListener('touchstart', onFirstInteract, { once: false });
    return () => {
      el.removeEventListener('pointerdown', onFirstInteract);
      el.removeEventListener('touchstart', onFirstInteract);
    };
  }, [startCompassIfNeeded]);

  // Mark dirty whenever data changes after initial render
  const originalData = useRef(data);
  useEffect(() => {
    if (data !== originalData.current) dirty.current = true;
  }, [data]);

  // Warn before page refresh / close when there's unsaved work
  useEffect(() => {
    const handler = (e) => {
      if (!dirty.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Ctrl+S to save
  const handleSaveRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!saving) handleSaveRef.current?.('save');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saving]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSubstrates = [...new Set(safe(data.surfaceEntries).filter(e=>!e.repeatGroupId).map(e=>e.substrate))];
  // Unified ordered item list — drives both chip display and measure sequence
  const activeItems = (() => {
    if (data.itemOrder && data.itemOrder.length > 0) return data.itemOrder;
    // Fallback for areas saved before itemOrder existed
    return [
      ...activeSubstrates.map(s => ({ type: 'surface', substrate: s })),
      ...(data.repeatGroups||[]).map(g => ({ type: 'group', groupId: g.id })),
    ];
  })();
  // Keep ref always fresh so async callbacks see latest items
  const activeItemsRef = useRef(activeItems);
  activeItemsRef.current = activeItems;
  const isCustom = surfDropdown === "Other" || surfDropdown === "Custom Surface Name";

  // ── Photo handler ────────────────────────────────────────────────────────
  // Accepts a File from a native <input type="file"> picker (camera or library).
  // Direction is captured from compassHeading at the moment the picker returns
  // and applied to every file in the batch.
  const handlePhoto = (file, direction) => {
    if (!file) return;
    setUploadCount(c => c + 1);

    uploadPhoto(
      file,
      projectId,
      // onPreview — show placeholder immediately
      (tempPhoto) => {
        let preview = direction ? { ...tempPhoto, direction } : tempPhoto;
        if (pendingSubstrateTag.current) {
          preview = { ...preview, substrate: pendingSubstrateTag.current };
        }
        setData(d => ({ ...d, photos: [...(d.photos || []), preview] }));
      },
      // onComplete — swap placeholder for real photo with both URLs
      (tempId, { url, thumbnailUrl }) => {
        setData(d => ({
          ...d,
          photos: (d.photos || []).map(x =>
            x.id === tempId
              ? { ...x, url, thumbnailUrl, pending: false }
              : x
          ),
        }));
        setUploadCount(c => c - 1);
      },
      // onError — remove placeholder and alert
      (tempId) => {
        showToast('Photo upload failed — please try again', 'error');
        setData(d => ({ ...d, photos: (d.photos || []).filter(x => x.id !== tempId) }));
        setUploadCount(c => c - 1);
      }
    );
  };
  // Handle onChange from a native file input — iterate files and apply the
  // current compass direction to the whole batch. Called from both camera and
  // library hidden inputs.
  const handleFileInput = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const heading = compassHeading.current;
    const direction = heading != null ? headingToDirection(heading) : null;
    files.forEach(f => handlePhoto(f, direction));
    // Clear after the batch so subsequent general captures aren't mis-tagged.
    pendingSubstrateTag.current = null;
  };
  const setPhotoDirection = (photoId, direction) => {
    setData(d => ({...d, photos: (d.photos||[]).map(p => p.id === photoId ? {...p, direction} : p)}));
  };

  // Does this surface already have at least one tagged condition photo?
  const substrateHasPhoto = (substrate) =>
    (data.photos || []).some(p => p.substrate === substrate && !p.pending);

  // Capture a photo pre-bound to a substrate (auto-tags on upload).
  // Reuses the existing hidden camera input; we stash the target substrate so
  // handleFileInput can tag the resulting photo(s).
  const pendingSubstrateTag = useRef(null);
  const captureForSubstrate = (substrate) => {
    pendingSubstrateTag.current = substrate || null;
    cameraInputRef.current?.click();
  };

  // ── Entry CRUD ──────────────────────────────────────────────────────────
  const addEntry = (substrate, groupId, extraFields) => setData(d => ({...d, surfaceEntries:[...safe(d.surfaceEntries), {...emptyEntry(substrate), repeatGroupId: groupId||null, ...(extraFields||{})}]}));
  const updateEntry = (id, field, value) => setData(d => ({...d, surfaceEntries: safe(d.surfaceEntries).map(e => e.id===id ? {...e,[field]:value} : e)}));
  const removeEntry = (id) => setData(d => ({...d, surfaceEntries: safe(d.surfaceEntries).filter(e=>e.id!==id)}));
  const removeSubstrate = (substrate) => setData(d => ({
    ...d,
    surfaceEntries: safe(d.surfaceEntries).filter(e => !(e.substrate===substrate && !e.repeatGroupId)),
    itemOrder: (d.itemOrder||[]).filter(item => !(item.type==='surface' && item.substrate===substrate)),
  }));

  const renameSubstrate = (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    setData(d => {
      const oldSettings = d.surfaceSettings?.[oldName] || {};
      const newSettings = { ...d.surfaceSettings };
      newSettings[trimmed] = oldSettings;
      delete newSettings[oldName];
      return {
        ...d,
        surfaceEntries: safe(d.surfaceEntries).map(e =>
          e.substrate === oldName ? { ...e, substrate: trimmed } : e
        ),
        surfaceSettings: newSettings,
        itemOrder: (d.itemOrder||[]).map(item =>
          item.type === 'surface' && item.substrate === oldName
            ? { ...item, substrate: trimmed }
            : item
        ),
      };
    });
  };

  // ── Settings ────────────────────────────────────────────────────────────
  const updateSetting = (substrate, key, val) => setData(d => ({
    ...d, surfaceSettings: { ...d.surfaceSettings, [substrate]: { ...d.surfaceSettings?.[substrate], [key]: val } }
  }));

  const updateCondition = (substrate, key, value, userEdited = false) => setData(prev => {
    const prevEntry = prev.conditionBySubstrate?.[substrate] || {};
    const nextEntry = { ...prevEntry, [key]: value };
    // Mark the note as hand-edited so a later auto-generate won't silently overwrite it
    if (key === 'note' && userEdited) nextEntry.noteEdited = true;
    if (key === 'note' && !userEdited) nextEntry.noteEdited = false;
    // If condition cleared and nothing else worth keeping, drop the entry
    if (key === 'condition' && value === null && !nextEntry.note && !(nextEntry.defects && nextEntry.defects.length)) {
      const { [substrate]: _drop, ...rest } = (prev.conditionBySubstrate || {});
      return { ...prev, conditionBySubstrate: rest };
    }
    return {
      ...prev,
      conditionBySubstrate: {
        ...(prev.conditionBySubstrate || {}),
        [substrate]: nextEntry,
      },
    };
  });

  // ── Compound surface (repeat group) handlers ────────────────────────────
  const updateRepeatGroup = (id, field, value) => setData(d => ({
    ...d, repeatGroups: (d.repeatGroups||[]).map(g => g.id===id ? {...g, [field]: value} : g)
  }));
  const removeRepeatGroup = (id) => {
    setData(d => ({
      ...d,
      repeatGroups: (d.repeatGroups||[]).filter(g => g.id!==id),
      surfaceEntries: safe(d.surfaceEntries).filter(e => e.repeatGroupId!==id),
      itemOrder: (d.itemOrder||[]).filter(item => !(item.type==='group' && item.groupId===id)),
    }));
    if (activeItemKey === `group:${id}`) setActiveItemKey(null);
  };

  // ── Add surface handler ─────────────────────────────────────────────────
  const addSurfaceDirect = (name) => {
    if (!name) return;
    setSurfDropdown(''); setSurfCustomName('');
    setMeasuresDone(false);
    const alreadyInOrder = (data.itemOrder||[]).some(item => item.type==='surface' && item.substrate===name);
    if (!alreadyInOrder) {
      setData(d => ({
        ...d,
        surfaceEntries: [...safe(d.surfaceEntries), { ...emptyEntry(name), repeatGroupId: null }],
        itemOrder: [...(d.itemOrder||[]), { type: 'surface', substrate: name }],
      }));
    } else {
      addEntry(name);
    }
  };

  // Create an independent copy of a surface with its own settings (e.g. "Walls 2")
  const addIndependentSurface = (displayName, baseSurface) => {
    const base = baseSurface || displayName;
    // Find next available number: scan existing substrates for "Walls 2", "Walls 3" etc.
    const pattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(\\d+)?$`);
    let maxNum = 1;
    const allSubs = [...new Set(safe(data.surfaceEntries).map(e => e.substrate)), ...((data.itemOrder||[]).filter(i=>i.type==='surface').map(i=>i.substrate))];
    allSubs.forEach(s => {
      const m = s.match(pattern);
      if (m) maxNum = Math.max(maxNum, parseInt(m[1] || '1', 10));
    });
    const newName = `${base} ${maxNum + 1}`;
    setMeasuresDone(false);
    setData(d => ({
      ...d,
      surfaceEntries: [...safe(d.surfaceEntries), { ...emptyEntry(newName, base), repeatGroupId: null }],
      itemOrder: [...(d.itemOrder||[]), { type: 'surface', substrate: newName }],
      surfaceSettings: { ...d.surfaceSettings, [newName]: { baseSurface: base } },
    }));
    setActiveItemKey(`surface:${newName}`);
  };

  // Duplicate an existing repeat group with all its surface entries
  const duplicateRepeatGroup = (sourceGroupId) => {
    const sourceGroup = (data.repeatGroups || []).find(g => g.id === sourceGroupId);
    if (!sourceGroup) return;

    setMeasuresDone(false);
    const newGroupId = `rg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const sourceEntries = safe(data.surfaceEntries).filter(e => e.repeatGroupId === sourceGroupId);

    // Deep copy entries with new IDs, linked to the new group
    const copiedEntries = sourceEntries.map(e => ({
      ...e,
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      repeatGroupId: newGroupId,
    }));

    setData(d => ({
      ...d,
      repeatGroups: [
        ...(d.repeatGroups || []),
        { id: newGroupId, name: `${sourceGroup.name} (copy)`, count: 1 }
      ],
      surfaceEntries: [...safe(d.surfaceEntries), ...copiedEntries],
      itemOrder: [...(d.itemOrder || []), { type: 'group', groupId: newGroupId }],
    }));

    // Auto-expand the new group in edit mode
    setActiveItemKey(`group:${newGroupId}`);
  };

  // Add a custom compound group with blank name — user renames inline
  const addCustomGroup = () => {
    setMeasuresDone(false);
    const id = `rg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const existingCustom = (data.repeatGroups||[]).filter(g => g.name === 'Custom Group' || g.name.startsWith('Custom Group '));
    const newName = existingCustom.length > 0 ? `Custom Group ${existingCustom.length + 1}` : 'Custom Group';
    setData(d => ({
      ...d,
      repeatGroups: [...(d.repeatGroups||[]), { id, name: newName, count: 1 }],
      itemOrder: [...(d.itemOrder||[]), { type: 'group', groupId: id }],
    }));
    setNewGroupId(id); // auto-focuses the name input so user renames immediately
  };

  const handleAddSurface = () => {
    const name = isCustom ? surfCustomName.trim() : surfDropdown;
    addSurfaceDirect(name);
  };

  // Auto-assign first photo as hero if none selected
  const ensureHero = (d) => {
    const photos = d.photos || [];
    // Only auto-promote if no hero exists in this area AND no other area in the project has one
    if (photos.length > 0 && !photos.some(p => p.isProjectHero) && !projectHasHero) {
      return { ...d, photos: photos.map((p, i) => ({ ...p, isProjectHero: i === 0 })) };
    }
    return d;
  };

  const handleSave = async (mode) => {
    if (!data.name.trim()) { setNameError(true); return; }
    setNameError(false);
    const saveData = ensureHero(data);
    setData(saveData);
    setSaving(true);
    if (mode === 'next') {
      setShowTypePicker(true);
      dirty.current = false;
      // Save in background — don't block the type picker
      onSave(saveData, measuresDone ? 'finished_stay' : 'stay')
        .catch(err => console.error('Background save failed:', err))
        .finally(() => setSaving(false));
      return;
    }
    await onSave(saveData, mode);
    dirty.current = false;
    setSaving(false);
  };

  handleSaveRef.current = handleSave;

  // Back — just leave if nothing worth saving
  const handleBack = async () => {
    const hasContent = data.name.trim() && (safe(data.surfaceEntries).length > 0 || (data.photos || []).length > 0);
    if (!hasContent) { dirty.current = false; onBack(); return; }
    const saveData = ensureHero(data);
    setData(saveData);
    setSaving(true);
    try {
      await onSave(saveData, measuresDone ? 'finished' : 'save');
      dirty.current = false;
    } catch (err) {
      console.error('handleBack save failed:', err);
      setSaving(false);
      showToast('Save failed — please try again', 'error');
    }
    setSaving(false);
  };

  // Scroll helper — uses panel on desktop (own overflow container), window on mobile
  const scrollToEl = (el) => {
    const headerHeight = 64;
    if (dataPanelRef.current) {
      const panelTop = dataPanelRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const offset = elTop - panelTop + dataPanelRef.current.scrollTop - headerHeight - 8;
      dataPanelRef.current.scrollTo({ top: offset, behavior: 'smooth' });
    } else {
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const advanceToNext = (currentKey) => {
    const items = activeItemsRef.current;
    const idx = items.findIndex(item =>
      item.type === 'surface' ? `surface:${item.substrate}` === currentKey : `group:${item.groupId}` === currentKey
    );
    if (idx >= 0 && idx < items.length - 1) {
      const next = items[idx + 1];
      setActiveItemKey(next.type === 'surface' ? `surface:${next.substrate}` : `group:${next.groupId}`);
    } else {
      // All measured — scroll to notes
      setActiveItemKey(null);
      setMeasuresDone(true);
      setTimeout(() => {
        if (notesRef.current) scrollToEl(notesRef.current);
      }, 120);
    }
  };

  const handleSaveNextSurface = async () => {
    if (!data.name.trim()) { setNameError(true); return; }
    setNameError(false);
    const saveData = ensureHero(data);
    setData(saveData);
    setSaving(true);
    try {
      await onSave(saveData, 'stay');
      dirty.current = false;
      if (!activeItemKey && activeItems.length > 0) {
        const first = activeItems[0];
        setActiveItemKey(first.type === 'surface' ? `surface:${first.substrate}` : `group:${first.groupId}`);
      } else {
        advanceToNext(activeItemKey);
      }
    } catch {}
    setSaving(false);
  };

  // ── JSX blocks ──────────────────────────────────────────────────────────

  const pmAlertJsx = showPMAlert ? (
    <ConfirmModal
      title={area?.pmAdded ? "⚠️ Area added by PM" : "⚠️ Surfaces added by PM"}
      message={`${area?.pmAdded ? `Your Project Manager added this area and` : 'Your Project Manager'} flagged ${unmeasuredPMSurfaces.length} surface${unmeasuredPMSurfaces.length > 1 ? 's' : ''} that need measurements:\n\n${unmeasuredPMSurfaces.map(s => `• ${s}`).join('\n')}${area?.pmNote ? `\n\n📝 PM Note: ${area.pmNote}` : ''}\n\n${unmeasuredPMSurfaces.length > 1 ? 'These have' : 'This has'} been added to your surface list — please enter the dimensions.`}
      confirmLabel="Got it"
      onConfirm={() => setShowPMAlert(false)}
      onCancel={() => setShowPMAlert(false)}
    />
  ) : null;

  const lightboxJsx = lightboxUrl ? <div className="s2s-lightbox" onClick={()=>setLightboxUrl(null)}><button className="s2s-lb-close" aria-label="Close lightbox"><Icon name="close" size={18} /></button><img src={lightboxUrl} alt="full" /></div> : null;

  const photoSectionJsx = (
    <div className="s2s-section">
      <div className="s2s-section-label">📷 Photos</div>
      {(data.photos||[]).length > 0 && (
        <div className="s2s-photo-row">
          {data.photos.map(p => (
            <div key={p.id} className="s2s-photo-cell">
              <div className={`s2s-photo-thumb ${p.isProjectHero?"s2s-photo-hero":""} ${p.isDefect?"s2s-photo-defect":""} ${p.pending?"s2s-photo-pending":""}`}>
                <img src={p.url} alt="site" onClick={()=>{ if (!p.pending) setLightboxUrl(p.url); }} />
                {p.pending && <div className="s2s-photo-uploading"><span className="spinner spinner-sm spinner-white"></span> Uploading…</div>}
                {!p.pending && <button className="s2s-photo-remove" aria-label="Remove photo" onClick={()=>setData(d=>({...d,photos:d.photos.filter(x=>x.id!==p.id)}))}><Icon name="close" size={10} /></button>}
                {!p.pending && <button className={`s2s-photo-star ${p.isProjectHero?"active":""}`} aria-label={p.isProjectHero?"Remove as cover photo":"Set as cover photo"} onClick={()=>setData(d=>({...d,photos:d.photos.map(x=>({...x,isProjectHero:x.id===p.id?!x.isProjectHero:false}))}))}>★</button>}
                {!p.pending && <button className={`s2s-photo-defect-btn ${p.isDefect?"active":""}`} title="Tag as defect" aria-label={p.isDefect?"Remove defect tag":"Tag as defect"} onClick={()=>setData(d=>({...d,photos:d.photos.map(x=>({...x,isDefect:x.id===p.id?!(x.isDefect||false):(x.isDefect||false)}))}))}>⚠</button>}
              </div>
              {!p.pending && (
                <select
                  className="s2s-photo-surface-tag"
                  title="Which surface does this photo show?"
                  value={p.substrate || ''}
                  onChange={e => {
                    const sub = e.target.value || null;
                    setData(d => ({ ...d, photos: d.photos.map(x => x.id === p.id ? { ...x, substrate: sub } : x) }));
                  }}
                >
                  <option value="">General photo</option>
                  {activeSubstrates.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="s2s-photo-buttons">
        <button className="s2s-btn-photo" onClick={()=>{ cameraInputRef.current?.click(); }}>📷 Camera</button>
        <button className="s2s-btn-photo" onClick={()=>{ libraryInputRef.current?.click(); }}>📁 Library</button>
      </div>
      {uploadCount > 0 && <div className="s2s-upload-status"><span className="spinner spinner-sm"></span> Uploading {uploadCount} photo{uploadCount !== 1 ? 's' : ''}…</div>}
      {(data.photos||[]).some(p=>p.isProjectHero) && <p className="s2s-hero-hint">★ Cover photo selected — will appear on the PDF front page</p>}
    </div>
  );

  const surfaceSelectorJsx = (
    <div className="s2s-section">
      <div className="s2s-section-label">🎨 Surfaces in Scope</div>
      {/* Unified chip list — simple and compound surfaces in add order */}
      {activeItems.map(item => {
        if (item.type === 'surface') {
          const entries = safe(data.surfaceEntries).filter(e => e.substrate === item.substrate && !e.repeatGroupId);
          const hasMeas = entries.some(e => entryM2(item.substrate, e) > 0);
          return (
            <div key={item.substrate} className={`s2s-selected-surface ${hasMeas ? 's2s-surface-measured' : 's2s-surface-pending'}`}>
              <span>{hasMeas ? '✓' : '○'} {item.substrate}</span>
              <button onClick={() => removeSubstrate(item.substrate)} aria-label={`Remove ${item.substrate}`}>✕</button>
            </div>
          );
        }
        // Compound surface chip — name is editable inline
        const grp = (data.repeatGroups||[]).find(g => g.id === item.groupId);
        if (!grp) return null;
        const grpEntries = safe(data.surfaceEntries).filter(e => e.repeatGroupId === grp.id);
        const grpHasMeas = grpEntries.some(e => entryM2(e.substrate, e) > 0);
        return (
          <div key={item.groupId} className={`s2s-selected-surface s2s-compound-surface ${grpHasMeas ? 's2s-surface-measured' : 's2s-surface-pending'}`}>
            <span className="s2s-compound-icon">{grpHasMeas ? '✓' : '⊞'}</span>
            <input className="s2s-compound-name" value={grp.name}
              placeholder="e.g. Balconies, Rooms, Garages"
              autoFocus={newGroupId === grp.id}
              ref={el => { if (newGroupId === grp.id && el) { el.focus(); el.select(); setNewGroupId(null); } }}
              onChange={e => updateRepeatGroup(grp.id, 'name', e.target.value)} />
            <button onClick={() => removeRepeatGroup(grp.id)} aria-label="Remove group">✕</button>
          </div>
        );
      })}
      {/* Tag-style surface picker */}
      <div className="af-tag-picker">
        {cfg.surfaces.filter(s => s !== 'Custom Surface Name').map(s => {
          const added = activeItems.some(i => i.type === 'surface' && i.substrate === s);
          return (
            <button key={s} className={`af-tag ${added ? 'af-tag-active' : ''}`}
              onClick={() => addSurfaceDirect(s)}>
              {added ? '✓ ' : '＋ '}{s}
            </button>
          );
        })}
        <button className={`af-tag af-tag-custom ${isCustom ? 'af-tag-active' : ''}`}
          onClick={() => { setSurfDropdown(surfDropdown === 'Custom Surface Name' ? '' : 'Custom Surface Name'); setSurfCustomName(''); }}>
          ✎ Custom Surface…
        </button>
        <button className="af-tag af-tag-compound"
          onClick={addCustomGroup} title="Repeating surface group — e.g. Balconies, Rooms, Garages">
          ⊞ Custom Group…
        </button>
      </div>
      <div className="af-tag-hints">
        <span>✎ = one-off surface &nbsp;·&nbsp; ⊞ = repeating group (e.g. 12 Balconies, 6 Rooms)</span>
      </div>
      {isCustom && (
        <div className="s2s-custom-input" style={{marginTop:8}}>
          <input autoFocus placeholder="Enter surface name..." value={surfCustomName} onChange={e=>setSurfCustomName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&surfCustomName.trim()&&handleAddSurface()} />
          <button onClick={handleAddSurface} disabled={!surfCustomName.trim()}>Add</button>
        </div>
      )}
      {activeItems.length > 0 && !activeItemKey && !measuresDone && (
        <button className="af-start-measuring-btn" onClick={() => {
          const first = activeItems[0];
          setActiveItemKey(first.type === 'surface' ? `surface:${first.substrate}` : `group:${first.groupId}`);
          setMeasuresDone(false);
        }}>
          <Icon name="ruler" size={16} /> Start adding measurements
        </button>
      )}
    </div>
  );

    const measurementsJsx = (
    <div className="af-measurements">
      {activeItems.length === 0
        ? <div className="af-no-surfaces">← Select surfaces to start measuring</div>
        : activeItems.map((item, idx) => {
            const isLastItem = idx === activeItems.length - 1;
            const nextItem = activeItems[idx + 1];
            const nextLabel = nextItem
              ? nextItem.type === 'surface' ? nextItem.substrate
                : (data.repeatGroups||[]).find(g=>g.id===nextItem.groupId)?.name || 'Next'
              : null;

            if (item.type === 'surface') {
              const substrate = item.substrate;
              const isExpanded = activeItemKey === `surface:${substrate}`;
              return (
                <div key={substrate} ref={el => { surfaceRefs.current[substrate] = el; }}>
                  <SurfaceBlock substrate={substrate} data={data} areaType={type}
                    isExpanded={isExpanded}
                    onToggleExpand={() => setActiveItemKey(k => k===`surface:${substrate}` ? null : `surface:${substrate}`)}
                    onUpdate={updateEntry} onAdd={addEntry} onRemove={removeEntry}
                    onSettingChange={updateSetting} onConditionChange={updateCondition} onRemoveSurface={() => removeSubstrate(substrate)}
                    substrateHasPhoto={substrateHasPhoto} onCaptureForSubstrate={captureForSubstrate}
                    onAddIndependent={addIndependentSurface} onRenameSurface={renameSubstrate} />
                  {isExpanded && (
                    <button
                      className={`af-next-surface-btn ${isLastItem ? 'af-next-surface-btn-last' : ''}`}
                      disabled={saving}
                      onClick={async () => {
                        if (!data.name.trim()) { setNameError(true); return; }
                        const saveData = ensureHero(data);
                        setData(saveData);
                        setSaving(true);
                        try { await onSave(saveData, 'stay'); dirty.current = false; } catch {}
                        setSaving(false);
                        advanceToNext(`surface:${substrate}`);
                      }}
                    >
                      {saving ? <><span className="spinner spinner-sm spinner-white"></span> Saving…</> : isLastItem ? <><Icon name="check" size={16} /> All surfaces done</> : <>Next: {nextLabel} <Icon name="chevron-right" size={16} /></>}
                    </button>
                  )}
                </div>
              );
            }

            // Compound surface
            const grp = (data.repeatGroups||[]).find(g => g.id === item.groupId);
            if (!grp) return null;
            return (
              <CompoundMeasureBlock key={grp.id}
                group={grp} data={data} surfaceList={cfg.surfaces} areaType={type}
                onUpdateGroup={updateRepeatGroup} onRemoveGroup={removeRepeatGroup}
                onDuplicateGroup={duplicateRepeatGroup}
                onAddEntry={addEntry} onUpdateEntry={updateEntry} onRemoveEntry={removeEntry}
                onSettingChange={updateSetting} onConditionChange={updateCondition}
                substrateHasPhoto={substrateHasPhoto} onCaptureForSubstrate={captureForSubstrate}
                isActive={activeItemKey === `group:${grp.id}`}
                saving={saving}
                onSave={async () => {
                  if (!data.name.trim()) return;
                  const saveData = ensureHero(data);
                  setData(saveData);
                  setSaving(true);
                  try { await onSave(saveData, 'stay'); dirty.current = false; } catch {}
                  setSaving(false);
                }}
                onDone={() => advanceToNext(`group:${grp.id}`)} />
            );
          })
      }
    </div>
  );

    const isLastSurface = !activeItemKey || activeItems.findIndex(item => (item.type==='surface' ? `surface:${item.substrate}` : `group:${item.groupId}`) === activeItemKey) >= activeItems.length - 1;

  const saveButtonsJsx = (
    <div className="s2s-save-section">
      {!isLastSurface ? (
        <>
          <button className="s2s-btn-next-surface" onClick={handleSaveNextSurface} disabled={saving}>
            {saving ? <span className="spinner spinner-sm spinner-white"></span> : 'Save → Next Surface'}
          </button>
          <button className="s2s-btn-save-back" onClick={() => handleSave("save")} disabled={saving}>
            {saving ? <span className="spinner spinner-sm"></span> : 'Save & Back'}
          </button>
        </>
      ) : (
        <>
          {/* Desktop review flow: step straight into the next area instead of
              going back out to the project hub between every area. */}
          {desktop && !isNew && nextArea && (
            <button className="s2s-btn-next-area" onClick={() => handleSave("next_area")} disabled={saving}
              title={`Save and open ${nextArea.name || 'the next area'}`}>
              {saving ? <span className="spinner spinner-sm spinner-white"></span> : (
                <>Save → Next Area<span className="s2s-next-area-name">{nextArea.type === 'internal' ? '🏠' : '🏗️'} {nextArea.name || 'Unnamed'}</span></>
              )}
            </button>
          )}
          <button className="s2s-btn-next-surface" onClick={() => handleSave(measuresDone ? "finished" : "save")} disabled={saving}>
            {saving ? <span className="spinner spinner-sm"></span> : measuresDone ? 'Done — Save & Back' : 'Save & Back'}
          </button>
          <button className="s2s-btn-save-back" onClick={() => handleSave("next")} disabled={saving}>
            {saving ? <span className="spinner spinner-sm spinner-white"></span> : 'Save → New Area'}
          </button>
        </>
      )}
    </div>
  );

  const notesJsx = (
    <div className="s2s-section" ref={notesRef}>
      {measuresDone && (
        <div className="af-status-summary">
          <div className="af-status-title">Measurements done — review before saving</div>
          <div className="af-status-pills">
            <div className={`af-status-pill ${(data.photos||[]).length > 0 ? 'af-status-done' : 'af-status-pending'}`}>
              {(data.photos||[]).length > 0 ? `✓ ${(data.photos||[]).length} photo${(data.photos||[]).length !== 1 ? 's' : ''}` : '○ No photos'}
            </div>
            <div className={`af-status-pill ${(data.scopeNotes||'').trim() ? 'af-status-done' : 'af-status-pending'}`}
              onClick={() => { if (!(data.scopeNotes||'').trim() && notesRef.current) scrollToEl(notesRef.current); }}>
              {(data.scopeNotes||'').trim() ? '✓ Scope notes' : '○ Scope notes'}
            </div>
            <div className={`af-status-pill ${(data.scopeExclusions||'').trim() ? 'af-status-done' : 'af-status-pending'}`}
              onClick={() => { if (!(data.scopeExclusions||'').trim() && notesRef.current) scrollToEl(notesRef.current); }}>
              {(data.scopeExclusions||'').trim() ? '✓ Exclusions' : '○ Exclusions'}
            </div>
            <div className={`af-status-pill ${(data.defectsNote||'').trim() ? 'af-status-done' : 'af-status-pending'}`}
              onClick={() => { if (!(data.defectsNote||'').trim() && notesRef.current) scrollToEl(notesRef.current); }}>
              {(data.defectsNote||'').trim() ? '✓ Defects' : '○ Defects'}
            </div>
            <div className={`af-status-pill ${(data.accessNeeds||[]).length > 0 ? 'af-status-done' : 'af-status-pending'}`}
              onClick={() => { if ((data.accessNeeds||[]).length === 0 && notesRef.current) scrollToEl(notesRef.current); }}>
              {(data.accessNeeds||[]).length > 0 ? '✓ Access & safety' : '○ Access & safety'}
            </div>
            <div className={`af-status-pill ${(data.downtime ?? 10) !== 10 ? 'af-status-done' : 'af-status-neutral'}`}>
              ⏱ {data.downtime ?? 10}% downtime
            </div>
          </div>
        </div>
      )}
      <div className="s2s-section-label">💬 Scope Notes</div>
      <textarea className="s2s-notes" rows={4} placeholder="e.g. 2 coats Dulux Wash & Wear" value={data.scopeNotes} onChange={e=>setData({...data,scopeNotes:e.target.value})} />
      <div className="s2s-section-label s2s-exclusions-label" style={{marginTop:12}}>🚫 Exclusions</div>
      <textarea className="s2s-notes s2s-exclusions" rows={3} placeholder="e.g. Feature wall excluded, no ceiling roses..." value={data.scopeExclusions||''} onChange={e=>setData({...data,scopeExclusions:e.target.value})} />
      <div className="s2s-section-label s2s-defects-label" style={{marginTop:12}}>⚠ Defects / Remediation Notes</div>
      <textarea className="s2s-notes s2s-defects-notes" rows={3} placeholder="e.g. Spalling concrete, cracked render..." value={data.defectsNote||''} onChange={e=>setData({...data,defectsNote:e.target.value})} />
      <div className="s2s-section-label" style={{marginTop:16}}>🦺 Access &amp; Safety Needs</div>
      <div className="s2s-access-hint">Flag anything that affects how the job is accessed or made safe. This appears in the scope report.</div>
      <AccessTags selected={data.accessNeeds||[]} onChange={v => setData({...data, accessNeeds: v})} areaType={type}
        otherLabels={data.accessOtherLabels||{}} onOtherLabelChange={(id, val) => setData(d => ({...d, accessOtherLabels: {...(d.accessOtherLabels||{}), [id]: val}}))} />
    </div>
  );

  const abseilPct = data.abseilPct ?? 0;
  const minDowntime = abseilPct > 0 ? 30 : 0;

  const downtimeJsx = (
    <div className="s2s-section dt-card">
      <div className="s2s-section-label">⏱ Downtime Allowance</div>
      <div className="dt-stepper">
        <button className="dt-btn" onClick={()=>setData(d=>({...d,downtime:Math.max(minDowntime,(d.downtime??10)-2)}))} disabled={(data.downtime??10)<=minDowntime}>−</button>
        <span className="dt-val">{data.downtime ?? 10}%</span>
        <button className="dt-btn" onClick={()=>setData(d=>({...d,downtime:Math.min(100,(d.downtime??10)+2)}))}>+</button>
      </div>
      {abseilPct > 0 && <p className="dt-hint" style={{color:'#E8871B'}}>Minimum 30% downtime when abseil work is included.</p>}
      <p className="dt-hint">Ladder footing, masking, setup. Exported to the downtime row in Excel.</p>
      <div className="s2s-section-label" style={{marginTop:14}}>🧗 Abseil</div>
      <div className="dt-stepper">
        <button className="dt-btn" onClick={()=>setData(d=>{
          const p = Math.max(0, (d.abseilPct??0) - 5);
          return {...d, abseilPct: p};
        })} disabled={abseilPct <= 0}>−</button>
        <span className="dt-val">{abseilPct}%</span>
        <button className="dt-btn" onClick={()=>setData(d=>{
          const p = Math.min(100, (d.abseilPct??0) + 5);
          const dt = d.downtime ?? 10;
          return {...d, abseilPct: p, downtime: Math.max(30, dt)};
        })}>+</button>
      </div>
      <p className="dt-hint">Percentage of this area that will be abseiled. Exported to Excel.</p>
    </div>
  );

  const prelimItems = data.preliminaryItems || [];
  const [prelimOtherText, setPrelimOtherText] = useState(() => {
    const custom = prelimItems.find(p => !PRELIMINARY_ITEMS.includes(p));
    return custom || '';
  });

  const togglePrelim = (item) => {
    dirty.current = true;
    setData(d => {
      const cur = d.preliminaryItems || [];
      return { ...d, preliminaryItems: cur.includes(item) ? cur.filter(i => i !== item) : [...cur, item] };
    });
  };

  const hasOther = prelimItems.some(p => !PRELIMINARY_ITEMS.includes(p));
  const toggleOther = () => {
    dirty.current = true;
    if (hasOther) {
      setData(d => ({ ...d, preliminaryItems: (d.preliminaryItems || []).filter(p => PRELIMINARY_ITEMS.includes(p)) }));
      setPrelimOtherText('');
    } else if (prelimOtherText.trim()) {
      setData(d => ({ ...d, preliminaryItems: [...(d.preliminaryItems || []), prelimOtherText.trim()] }));
    }
  };

  const prelimsJsx = (
    <div className="s2s-section prelim-card">
      <div className="s2s-section-label">📋 Preliminary Items</div>
      <p className="prelim-hint">Tag items needed for this area. Names export to the Excel Take Off &amp; Summary sheets — values are entered in Excel.</p>
      <div className="prelim-pills">
        {PRELIMINARY_ITEMS.map(item => (
          <button key={item}
            className={`prelim-pill ${prelimItems.includes(item) ? 'active' : ''}`}
            onClick={() => togglePrelim(item)}>
            {prelimItems.includes(item) ? '✓ ' : ''}{item}
          </button>
        ))}
        <button className={`prelim-pill prelim-other ${hasOther ? 'active' : ''}`} onClick={toggleOther}>
          {hasOther ? '✓ ' : ''}Other
        </button>
      </div>
      {(hasOther || prelimOtherText) && (
        <input className="prelim-other-input" type="text" placeholder="e.g. Crane hire, Hoisting..."
          value={prelimOtherText}
          onChange={e => {
            const v = e.target.value;
            setPrelimOtherText(v);
            // Live update: remove old custom, add new if non-empty
            setData(d => {
              const standard = (d.preliminaryItems || []).filter(p => PRELIMINARY_ITEMS.includes(p));
              return { ...d, preliminaryItems: v.trim() ? [...standard, v.trim()] : standard };
            });
          }} />
      )}
    </div>
  );

  // ── Desktop layout ──────────────────────────────────────────────────────
  if (desktop) {
    const photos = data.photos||[];
    const hasPhotos = photos.length > 0;
    return (
      <div className="s2s-screen" ref={formRef} onKeyDown={autoAdvance}>
        {lightboxJsx}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={e => { handleFileInput(e.target.files); e.target.value = ''; }}
        />
        <input
          ref={libraryInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={e => { handleFileInput(e.target.files); e.target.value = ''; }}
        />
        {pmAlertJsx}
        {showTypePicker && (
          <div className="cm-overlay" onClick={() => setShowTypePicker(false)}>
            <div className="cm-dialog" onClick={e => e.stopPropagation()}>
              <div className="cm-title">Next area type</div>
              <div className="cm-message">What type of area are you measuring next?</div>
              <div className="cm-buttons">
                <button className="cm-btn cm-confirm" onClick={() => { setShowTypePicker(false); onNewArea('internal', data.name); }}>🏠 Internal</button>
                <button className="cm-btn cm-confirm" style={{background:'#E8871B'}} onClick={() => { setShowTypePicker(false); onNewArea('external', data.name); }}>🏗️ External</button>
              </div>
            </div>
          </div>
        )}
        <div className="s2s-header">
          <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
          <div className="s2s-header-center">
            <div className="s2s-header-title">{isNew?"New":"Edit"} {cfg.icon} {cfg.label} Area</div>
            {data.name && <div className="s2s-header-sub">{data.name}{areaPosition ? ` · Area ${areaPosition.index} of ${areaPosition.total}` : ''}</div>}
          </div>
          <div style={{width:80}} />
        </div>
        <div className={`af-desktop-split ${hasPhotos?"af-has-photos":"af-no-photos"}`}>
          {hasPhotos && (
            <div className="af-photo-panel">
              <div className="af-photo-main">
                {photos.length > 1 && <button className="af-photo-nav af-photo-prev" onClick={()=>setSelectedPhoto(i=>(i-1+photos.length)%photos.length)}>‹</button>}
                <img src={photos[selectedPhoto]?.url||photos[0].url} alt="site" onClick={()=>setLightboxUrl((photos[selectedPhoto]||photos[0]).url)} />
                {photos.length > 1 && <button className="af-photo-nav af-photo-next" onClick={()=>setSelectedPhoto(i=>(i+1)%photos.length)}>›</button>}
                <div className="af-photo-count">{(selectedPhoto||0)+1} / {photos.length}</div>
                {(photos[selectedPhoto]||photos[0])?.direction && <div className="af-photo-dir-main">🧭 {(photos[selectedPhoto]||photos[0]).direction}</div>}
              </div>
              {photos.length > 1 && (
                <div className="af-photo-thumbs">
                  {photos.map((p,i) => (
                    <div key={p.id} className={`af-photo-thumb-item ${selectedPhoto===i?"active":""} ${p.isProjectHero?"af-photo-hero":""} ${p.isDefect?"af-photo-defect":""}`} onClick={()=>setSelectedPhoto(i)}>
                      <img src={p.url} alt="" />
                      {p.isProjectHero && <div className="af-hero-badge">★</div>}
                      {p.isDefect && <div className="af-defect-badge">⚠ Defect</div>}
                      {p.direction && <div className="af-dir-badge">{p.direction}</div>}
                      <button className="s2s-photo-remove" aria-label="Remove photo" onClick={e=>{e.stopPropagation();setData(d=>({...d,photos:d.photos.filter(x=>x.id!==p.id)}));if(selectedPhoto>=photos.length-1)setSelectedPhoto(Math.max(0,photos.length-2));}}><Icon name="close" size={10} /></button>
                      <button className={`af-star-btn ${p.isProjectHero?"active":""}`} aria-label={p.isProjectHero?"Remove as cover photo":"Set as cover photo"} onClick={e=>{e.stopPropagation();setData(d=>({...d,photos:d.photos.map(x=>({...x,isProjectHero:x.id===p.id?!x.isProjectHero:false}))}));}}>★</button>
                      <button className={`af-defect-btn ${p.isDefect?"active":""}`} title="Tag as defect" aria-label={p.isDefect?"Remove defect tag":"Tag as defect"} onClick={e=>{e.stopPropagation();setData(d=>({...d,photos:d.photos.map(x=>({...x,isDefect:x.id===p.id?!(x.isDefect||false):(x.isDefect||false)}))}))}}>⚠</button>
                    </div>
                  ))}
                </div>
              )}
              {/* Direction picker for selected photo */}
              <div className="af-photo-dir-row">
                <span className="af-photo-dir-label">🧭 Direction:</span>
                <DirectionPicker value={(photos[selectedPhoto]||photos[0])?.direction||null} onChange={v => setPhotoDirection((photos[selectedPhoto]||photos[0]).id, v)} />
              </div>
              {photos.length===1 && <button className="af-photo-remove-single" onClick={()=>{setData(d=>({...d,photos:[]}));setSelectedPhoto(0);}}><Icon name="close" size={12} /> Remove photo</button>}
              <div className="af-photo-add-row">
                <button className="s2s-btn-photo" onClick={()=>{ cameraInputRef.current?.click(); }} style={{flex:1}}>📷 Camera</button>
                <button className="s2s-btn-photo" onClick={()=>{ libraryInputRef.current?.click(); }} style={{flex:1}}>📁 Library</button>
              </div>
              {uploadCount > 0 && <div className="s2s-upload-status"><span className="spinner spinner-sm"></span> Uploading {uploadCount} photo{uploadCount !== 1 ? 's' : ''}…</div>}
            </div>
          )}
          <div className="af-data-panel" ref={dataPanelRef}>
            {!hasPhotos && (
              <div className="af-upload-prompt">
                <button className="s2s-btn-photo" onClick={()=>{ cameraInputRef.current?.click(); }}>📷 Camera</button>
                <button className="s2s-btn-photo" onClick={()=>{ libraryInputRef.current?.click(); }}>📁 Library</button>
              </div>
            )}
            <div className="s2s-section">
              <div className={`s2s-field ${nameError ? 's2s-field-error' : ''}`}><label>Area Name *</label><input autoFocus placeholder={cfg.nameHint} value={data.name} onChange={e=>{setData({...data,name:e.target.value});setNameError(false);}} />{nameError && <div className="s2s-field-error-msg">Please enter an area name to continue</div>}</div>
            </div>
            {surfaceSelectorJsx}
            {measurementsJsx}
            {notesJsx}
            {downtimeJsx}
            {prelimsJsx}
            {saveButtonsJsx}
            <div style={{height:40}} />
          </div>
        </div>
      </div>
    );
  }

  // ── Mobile layout ───────────────────────────────────────────────────────
  return (
    <div className="s2s-screen" ref={formRef} onKeyDown={autoAdvance}>
      {lightboxJsx}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={e => { handleFileInput(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => { handleFileInput(e.target.files); e.target.value = ''; }}
      />
      {pmAlertJsx}
      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">{isNew?"New":"Edit"} {cfg.icon} {cfg.label}</div>
          {data.name && <div className="s2s-header-sub">{data.name}</div>}
        </div>
        <div style={{width:60}} />
      </div>
      <div className="s2s-content">
        {showTypePicker && (
          <div className="cm-overlay" onClick={() => setShowTypePicker(false)}>
            <div className="cm-dialog" onClick={e => e.stopPropagation()}>
              <div className="cm-title">Next area type</div>
              <div className="cm-message">What type of area are you measuring next?</div>
              <div className="cm-buttons">
                <button className="cm-btn cm-confirm" onClick={() => { setShowTypePicker(false); onNewArea('internal', data.name); }}>🏠 Internal</button>
                <button className="cm-btn cm-confirm" style={{background:'#E8871B'}} onClick={() => { setShowTypePicker(false); onNewArea('external', data.name); }}>🏗️ External</button>
              </div>
            </div>
          </div>
        )}
        <div className="s2s-section">
          <div className={`s2s-field ${nameError ? 's2s-field-error' : ''}`}><label>Area Name *</label><input autoFocus placeholder={cfg.nameHint} value={data.name} onChange={e=>{setData({...data,name:e.target.value});setNameError(false);}} />{nameError && <div className="s2s-field-error-msg">Please enter an area name to continue</div>}</div>
        </div>
        {photoSectionJsx}
        {surfaceSelectorJsx}
        {measurementsJsx}
        {notesJsx}
        {downtimeJsx}
        {prelimsJsx}
        {saveButtonsJsx}
        <div style={{height:160}} />
      </div>
    </div>
  );
}
